import { useState } from 'react';
import './Roleplay.css';
import SEO from '../../SEO/SEO';
// Main component
export default function Roleplay() {
  // Track current scenario and conversation progress
  const [currentScenario, setCurrentScenario] = useState(0);
  const [dialogIndex, setDialogIndex] = useState(0);
  const [userScore, setUserScore] = useState(0);
  const [gameState, setGameState] = useState('menu'); // menu, playing, finished
  const [feedback, setFeedback] = useState('');

  // Scenario data
  const scenarios = [
    {
      title: "At the Coffee Shop",
      context: "You're ordering coffee at a local café.",
      dialog: [
        {
          speaker: "Barista",
          text: "Hi there! What can I get for you today?",
          options: [
            { text: "Give me a coffee.", score: 0, feedback: "This sounds a bit demanding. Try being more polite." },
            { text: "I'd like a medium latte, please.", score: 2, feedback: "Perfect! Polite and clear." },
            { text: "Um... coffee... I want... um...", score: 1, feedback: "A bit hesitant, but understandable." }
          ]
        },
        {
          speaker: "Barista",
          text: "Would you like anything else with that?",
          options: [
            { text: "No.", score: 0, feedback: "Very abrupt. Consider adding 'thank you' to be more polite." },
            { text: "Yes, could I also get a blueberry muffin please?", score: 2, feedback: "Great response! Polite and specific." },
            { text: "Maybe... what do you recommend?", score: 1, feedback: "This works, but a direct answer would be more efficient." }
          ]
        },
        {
          speaker: "Barista",
          text: "That'll be $8.50. How would you like to pay?",
          options: [
            { text: "I'll use my credit card.", score: 2, feedback: "Clear and direct response." },
            { text: "Oh no! I forgot my wallet!", score: 1, feedback: "This could happen, but isn't ideal in a transaction." },
            { text: "Why so expensive? That's ridiculous!", score: 0, feedback: "This response is confrontational and inappropriate." }
          ]
        }
      ]
    },
    {
      title: "Job Interview",
      context: "You're interviewing for a position at a marketing company.",
      dialog: [
        {
          speaker: "Interviewer",
          text: "Thanks for coming in today. Could you tell me a bit about yourself?",
          options: [
            { text: "Well, I graduated from university last year and I've been working on improving my design skills. I'm particularly interested in this position because it allows me to combine my creativity with marketing strategy.", score: 2, feedback: "Excellent response! Professional and relevant." },
            { text: "I'm 25 years old and I like design and stuff. I need a job.", score: 0, feedback: "Too casual and doesn't highlight your qualifications." },
            { text: "Um, I have some experience in marketing and I think I could do well here.", score: 1, feedback: "A good start, but could include more specific details about your background." }
          ]
        },
        {
          speaker: "Interviewer",
          text: "What would you say is your greatest strength?",
          options: [
            { text: "I'm never late and I don't call in sick.", score: 1, feedback: "Reliability is good, but this sounds like meeting minimum expectations." },
            { text: "I'm a great team player who also works well independently. I'm particularly skilled at coordinating projects and meeting deadlines.", score: 2, feedback: "Great! Specific strengths that are relevant to the workplace." },
            { text: "I don't know... I'm pretty good at everything I guess.", score: 0, feedback: "This shows a lack of self-awareness and preparation." }
          ]
        },
        {
          speaker: "Interviewer",
          text: "Do you have any questions for me about the position?",
          options: [
            { text: "No, I think you covered everything.", score: 0, feedback: "Missed opportunity to show interest in the role." },
            { text: "Yes, could you tell me more about the team I'd be working with and the typical projects I might be involved in?", score: 2, feedback: "Excellent! Shows genuine interest in the role." },
            { text: "How much vacation time do I get and what's the salary?", score: 1, feedback: "These are important questions but might be better for later in the process." }
          ]
        }
      ]
    },
    {
      title: "Shopping for Clothes",
      context: "You're looking for a new outfit at a clothing store.",
      dialog: [
        {
          speaker: "Sales Assistant",
          text: "Hello! Are you looking for something specific today?",
          options: [
            { text: "Just looking, thanks.", score: 1, feedback: "Polite but doesn't invite further assistance." },
            { text: "Yes, I need a new outfit for a job interview. Could you help me find something professional?", score: 2, feedback: "Perfect! Clear about your needs and asking for help." },
            { text: "Your clothes are too expensive.", score: 0, feedback: "This is unnecessarily negative and not a constructive way to start a conversation." }
          ]
        },
        {
          speaker: "Sales Assistant",
          text: "Have you seen our new collection? We have some great blazers that would be perfect for interviews.",
          options: [
            { text: "Where are they? I don't see them.", score: 1, feedback: "A bit abrupt, but communicates what you need." },
            { text: "Yes, I'd love to see them. What colors do you recommend for a professional look?", score: 2, feedback: "Excellent! Engaging with the assistant and asking for their expertise." },
            { text: "I don't like blazers. Show me something else.", score: 0, feedback: "This comes across as demanding. Consider phrasing as a question." }
          ]
        },
        {
          speaker: "Sales Assistant",
          text: "Would you like to try this one on? The fitting rooms are right over there.",
          options: [
            { text: "Yes, thank you. Could you also bring me a size larger just in case?", score: 2, feedback: "Great! Polite and making a clear request." },
            { text: "OK.", score: 0, feedback: "Very minimal. Adding 'thank you' would make this more polite." },
            { text: "I'll try it, but I'm not sure it's my style.", score: 1, feedback: "Honest response that keeps the conversation going." }
          ]
        }
      ]
    },
    {
      title: "Restaurant Dining",
      context: "You're having dinner at a restaurant with a friend.",
      dialog: [
        {
          speaker: "Server",
          text: "Good evening and welcome! Do you have a reservation?",
          options: [
            { text: "Yes, it should be under Lee for 7:30.", score: 2, feedback: "Perfect! Clear and provides all the necessary information." },
            { text: "No, we don't. Is that a problem?", score: 1, feedback: "Direct but slightly confrontational. A more friendly tone would be better." },
            { text: "You should check your book and tell me.", score: 0, feedback: "This sounds demanding and doesn't provide the server with needed information." }
          ]
        },
        {
          speaker: "Server",
          text: "Are you ready to order, or would you like a few more minutes?",
          options: [
            { text: "We need more time, sorry. Could you come back in five minutes?", score: 2, feedback: "Excellent! Polite and gives a specific timeframe." },
            { text: "I'm still deciding.", score: 1, feedback: "Brief but acceptable. Adding 'please' would make it more polite." },
            { text: "Why are you rushing us? We just sat down.", score: 0, feedback: "Unnecessarily confrontational. The server is just doing their job." }
          ]
        },
        {
          speaker: "Server",
          text: "How is everything tasting? Is there anything else I can get for you?",
          options: [
            { text: "The food is delicious, thank you! Could we get some more water, please?", score: 2, feedback: "Great! Providing feedback and making a polite request." },
            { text: "It's fine. No, we don't need anything.", score: 1, feedback: "A bit brief but communicates what's needed." },
            { text: "My steak is overcooked. I want to speak to the manager immediately.", score: 0, feedback: "This escalates the situation unnecessarily. Consider explaining the issue calmly first." }
          ]
        }
      ]
    },
    {
      title: "Doctor's Appointment",
      context: "You're visiting a doctor for a regular check-up.",
      dialog: [
        {
          speaker: "Doctor",
          text: "Good morning! What brings you in today?",
          options: [
            { text: "I've been having some headaches recently, and I wanted to get them checked out. They usually happen in the afternoon after work.", score: 2, feedback: "Excellent! Clear description with specific details." },
            { text: "Just a regular check-up. Nothing special.", score: 1, feedback: "Brief but adequate. More detail would help the doctor better." },
            { text: "I don't know, you're the doctor. You tell me what's wrong.", score: 0, feedback: "This is unhelpful and doesn't provide information the doctor needs." }
          ]
        },
        {
          speaker: "Doctor",
          text: "How often do you exercise, and what type of activities do you usually do?",
          options: [
            { text: "I try to go jogging three times a week and do some light weight training on weekends. Lately, I've been too busy to keep up with my routine though.", score: 2, feedback: "Perfect! Detailed and honest information." },
            { text: "I don't really exercise much.", score: 1, feedback: "Honest but brief. More detail would be helpful." },
            { text: "Why are you asking me that? It has nothing to do with my headaches.", score: 0, feedback: "Defensive response. The doctor needs a complete health picture." }
          ]
        },
        {
          speaker: "Doctor",
          text: "I'm going to prescribe something for your headaches. Do you have any questions about the medication?",
          options: [
            { text: "Yes, are there any side effects I should be aware of? And how long should I take it before scheduling a follow-up?", score: 2, feedback: "Excellent questions that show you're engaged in your healthcare." },
            { text: "No, I'll just take whatever you think is best.", score: 1, feedback: "Shows trust, but it's always good to be informed about medications." },
            { text: "Will this actually work? The last doctor gave me something useless.", score: 0, feedback: "Unnecessarily confrontational and doesn't focus on relevant information." }
          ]
        }
      ]
    },
    {
      title: "At a Hotel Reception",
      context: "You're checking into a hotel for a business trip.",
      dialog: [
        {
          speaker: "Receptionist",
          text: "Good afternoon! Welcome to Grand Hotel. How may I help you today?",
          options: [
            { text: "Hi, I'd like to check in, please. I have a reservation under the name Taylor.", score: 2, feedback: "Perfect! Polite greeting and provides necessary information." },
            { text: "I need a room. Is there one available?", score: 1, feedback: "Direct but doesn't mention reservation. A greeting would be more polite." },
            { text: "Finally! I've been standing here for ages waiting for service.", score: 0, feedback: "Unnecessarily rude. Starting with a complaint creates a negative interaction." }
          ]
        },
        {
          speaker: "Receptionist",
          text: "I see your reservation for five nights. Would you prefer a room on a higher or lower floor?",
          options: [
            { text: "A higher floor would be great if possible. I prefer the view and it's usually quieter.", score: 2, feedback: "Excellent response with a clear preference and reasoning." },
            { text: "Whatever is available is fine.", score: 1, feedback: "Acceptable but missing an opportunity to ensure your preferences are met." },
            { text: "I paid enough for this room, so I expect the best floor you have.", score: 0, feedback: "Demanding and implies dissatisfaction before even seeing the room." }
          ]
        },
        {
          speaker: "Receptionist",
          text: "Perfect! Your room is ready. Is there anything else you need to know about the hotel facilities?",
          options: [
            { text: "Yes, could you tell me what time breakfast is served and if the hotel has a gym I can use?", score: 2, feedback: "Great! Shows interest and asks specific, relevant questions." },
            { text: "No, that's all for now. Thanks.", score: 1, feedback: "Polite but brief. Asking about facilities might be helpful for your stay." },
            { text: "Just give me my key card. I'm tired and need to get to my room.", score: 0, feedback: "Abrupt and dismissive. Adding 'please' would make this more polite." }
          ]
        }
      ]
    },
    {
      title: "Public Transportation",
      context: "You're taking a bus in an unfamiliar city and need directions.",
      dialog: [
        {
          speaker: "You",
          text: "Excuse me, I need to get to the Central Museum. Does this bus go there?",
          options: [
            { text: "Thanks for your help! Can you tell me which stop I should get off at?", score: 2, feedback: "Excellent follow-up question that shows appreciation and asks for specific information." },
            { text: "Ok, I'll stay on then.", score: 1, feedback: "Brief but acceptable. Asking for more specific directions would be better." },
            { text: "Why couldn't you just say yes or no? That wasn't clear at all.", score: 0, feedback: "Unnecessarily rude and confrontational. The person is trying to help you." }
          ]
        },
        {
          speaker: "Passenger",
          text: "Get off at Central Square, then walk two blocks east. You can't miss it.",
          options: [
            { text: "Two blocks east... so is that left or right when I get off the bus?", score: 2, feedback: "Good clarification question that will help you find your way." },
            { text: "Ok.", score: 1, feedback: "Very brief. Confirming directions would be more helpful." },
            { text: "That doesn't help me. I'm not from here, I don't know which way east is!", score: 0, feedback: "Unnecessarily frustrated response. Asking for clarification would be more effective." }
          ]
        },
        {
          speaker: "Passenger",
          text: "It will be to your right when you exit the bus. The building has big columns in front.",
          options: [
            { text: "That's really helpful, thank you so much for your assistance!", score: 2, feedback: "Perfect! Shows genuine appreciation for the help." },
            { text: "Right, got it.", score: 1, feedback: "Confirms understanding but could be more appreciative." },
            { text: "I hope you're right. I don't want to get lost.", score: 0, feedback: "Implies distrust in the information provided. Unnecessary negative tone." }
          ]
        }
      ]
    },
    {
      title: "Team Meeting at Work",
      context: "You're participating in a weekly team meeting with colleagues.",
      dialog: [
        {
          speaker: "Manager",
          text: "Let's go around the table and get updates on everyone's projects. Would you like to start?",
          options: [
            { text: "Sure! I've completed the first phase of the marketing campaign and I'm on track to finish the second phase by Friday. I might need some input from the design team later this week.", score: 2, feedback: "Excellent! Clear, concise update with progress and future needs." },
            { text: "Um, I guess. Things are going fine with my project.", score: 1, feedback: "Too vague. Specific details about progress would be more useful." },
            { text: "Why do I always have to go first? Can someone else start this time?", score: 0, feedback: "Unnecessarily defensive and avoids providing an update." }
          ]
        },
        {
          speaker: "Colleague",
          text: "I think we should reconsider the approach we're taking on the Johnson project. The current strategy isn't working well.",
          options: [
            { text: "That's an interesting point. Could you share what specific aspects aren't working and what alternatives you're considering?", score: 2, feedback: "Excellent response that encourages constructive discussion." },
            { text: "Maybe, but we've already put a lot of work into the current approach.", score: 1, feedback: "Somewhat defensive but reasonable concern. Being more open to feedback would be better." },
            { text: "That's not true. My part of the project is fine. The problem must be on your end.", score: 0, feedback: "Unnecessarily confrontational and dismissive of a colleague's concern." }
          ]
        },
        {
          speaker: "Manager",
          text: "We need someone to present our quarterly results to the executive team next week. Any volunteers?",
          options: [
            { text: "I'd be happy to take that on. I've been closely involved with the project and feel confident I can highlight our key achievements.", score: 2, feedback: "Great! Shows initiative and confidence." },
            { text: "I could do it if no one else wants to.", score: 1, feedback: "Shows willingness but lacks confidence. A more positive tone would be better." },
            { text: "Not me. I hate presenting, and besides, that should be your job as the manager.", score: 0, feedback: "Unnecessarily negative and unprofessional response." }
          ]
        }
      ]
    }
  ];

  // Get current dialog based on state
  const currentDialog = gameState === 'playing' ? scenarios[currentScenario].dialog[dialogIndex] : null;

  // Handle option selection
  const handleOptionSelect = (option) => {
    setFeedback(option.feedback);
    setUserScore(userScore + option.score);
    
    // Check if this is the last dialog in the scenario
    if (dialogIndex >= scenarios[currentScenario].dialog.length - 1) {
      setGameState('finished');
    } else {
      // Move to next dialog after a short delay
      setTimeout(() => {
        setDialogIndex(dialogIndex + 1);
        setFeedback('');
      }, 2000);
    }
  };

  // Start a scenario
  const startScenario = (index) => {
    setCurrentScenario(index);
    setDialogIndex(0);
    setUserScore(0);
    setGameState('playing');
    setFeedback('');
  };

  // Return to menu
  const returnToMenu = () => {
    setGameState('menu');
    setFeedback('');
  };

  // Try another scenario
  const tryAnotherScenario = () => {
    const nextScenario = (currentScenario + 1) % scenarios.length;
    startScenario(nextScenario);
  };

  // Calculate max possible score for the current scenario
  const getMaxScore = () => {
    return scenarios[currentScenario].dialog.length * 2;
  };

  // Render menu
  if (gameState === 'menu') {
    return (
      <div className="Role-conversation-game">
          <SEO
      title="English Conversation Practice | Boldly English"
      description="Practice English conversation through interactive real-life scenarios, including job interviews, restaurants, shopping, travel, and workplace conversations."
      canonical="https://boldlyenglish.com/roleplay"
       />

        <h1 className='Role-h1'>English Conversation Practice</h1>
        <p className="Role-game-description">Choose a conversation scenario to practice your English communication skills.</p>
        
        <div className="Role-scenario-list">
          {scenarios.map((scenario, index) => (
            <div key={index} className="Role-scenario-card" onClick={() => startScenario(index)}>
              <h2 className='Role-h2'>{scenario.title}</h2>
              <p className='Role-p'>{scenario.context}</p>
              <button className="Role-start-button">Start Practice</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render game finished screen
  if (gameState === 'finished') {
    const maxScore = getMaxScore();
    const percentage = Math.round((userScore / maxScore) * 100);
    
    let evaluation;
    if (percentage >= 80) {
      evaluation = "Excellent! Your communication skills are very effective.";
    } else if (percentage >= 60) {
      evaluation = "Good job! You're on the right track with your communication.";
    } else {
      evaluation = "Keep practicing! You'll improve with more conversation practice.";
    }
    
    return (
      <div className="Role-conversation-game">
        <h1 className='Role-h1'>Scenario Complete!</h1>
        <h2 className='Role-h2'>{scenarios[currentScenario].title}</h2>
        
        <div className="Role-results-container">
          <p className='Role-p'>Your score: {userScore} out of {maxScore} ({percentage}%)</p>
          <p className="Role-evaluation">{evaluation}</p>
          
          <div className="Role-button-group">
            <button onClick={returnToMenu} className="Role-menu-button">Back to Menu</button>
            <button onClick={tryAnotherScenario} className="Role-next-button">Try Another Scenario</button>
          </div>
        </div>
      </div>
    );
  }

  // Render game playing screen
  return (
    <div className="Role-conversation-game">
      <h1 className='Role-h1'>{scenarios[currentScenario].title}</h1>
      <p className="Role-context">{scenarios[currentScenario].context}</p>
      
      <div className="Role-dialog-container">
        <div className="Role-dialog-box speaker">
          <p className="Role-speaker-name">{currentDialog.speaker}:</p>
          <p className="Role-speaker-text">{currentDialog.text}</p>
        </div>
        
        <div className="Role-options-container">
          <p className="Role-your-response">Your response:</p>
          {currentDialog.options.map((option, index) => (
            <button 
              key={index} 
              className="Role-option-button" 
              onClick={() => handleOptionSelect(option)}
            >
              {option.text}
            </button>
          ))}
        </div>
        
        {feedback && (
          <div className="Role-feedback-box">
            <p>{feedback}</p>
          </div>
        )}
      </div>
      
      <div className="Role-progress-indicator">
        Dialog {dialogIndex + 1} of {scenarios[currentScenario].dialog.length}
      </div>
      
      <button onClick={returnToMenu} className="Role-menu-button small">Back to Menu</button>
    </div>
  );
}